import { SPLIT_FLAGS } from 'Source/constants';
import { useTreatments } from '@splitsoftware/splitio-react';

/**
 * `useFeatureToggle` is a thin wrapper over the Split `useTreatments` hook. It returns
 * a single boolean value that indicates whether the treatment is set to "on" or not.
 * It also optionally accepts a default treatment boolean state to fall back to in the case Split
 * is unavailable.
 *
 * **NOTE:** This hook is not intended to fit 100% of Split use cases, and should not be
 * treated as such. Rather, it's a simple wrapper for 90% of use cases where we use
 * a treatment to enable/disable a feature.
 */
const useFeatureToggle = (toggleKey: keyof typeof SPLIT_FLAGS, defaultTreatment = false) => {
  const toggleName = SPLIT_FLAGS[toggleKey] ?? '';
  const { [toggleName]: flag } = useTreatments([toggleName]);
  if (flag?.treatment === 'control') {
    return defaultTreatment;
  }
  return flag?.treatment === 'on';
};

/**
 * `useFeatureToggleWithLoading` is the same as `useFeatureFlag` but returns whether the flag is loading
 */
export const useFeatureToggleWithLoading = (
  toggleKey: keyof typeof SPLIT_FLAGS,
  defaultTreatment = false,
) => {
  const toggleName = SPLIT_FLAGS[toggleKey] ?? '';
  const { [toggleName]: flag } = useTreatments([toggleName]);
  if (flag?.treatment === 'control') {
    return { enabled: defaultTreatment, loading: true };
  }
  const enabled = flag?.treatment === 'on';
  const loading = !flag;
  return { enabled, loading };
};

/**
 * Like `useFeatureToggle` except the toggle key is any arbitrary string. If no
 * split flag is found of that key, return defaultTreatment.
 */
export const useFeatureToggleDynamic = (toggleKey: string, defaultTreatment = false) => {
  const treatments = useTreatments([toggleKey]);
  const featureFlagTreatment = treatments[toggleKey];
  if (featureFlagTreatment?.treatment === 'control') {
    return defaultTreatment;
  }
  return featureFlagTreatment?.treatment === 'on';
};

export default useFeatureToggle;
